import { trackQuickFiltersEvents } from '@modules/charts/components/ChartTooltipWrapper/helpers/trackQuickFiltersEvents';
import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { CategoryLabelTooltipNode } from '@modules/common/components/CategoryLabelTooltipNode';
import { Category } from '@modules/common/types';
import { useAddQuickFilter } from '@modules/queryBuilder/hooks';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { FilterAltRounded } from '@mui/icons-material';
import {
  alpha,
  Box,
  IconButton,
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';
import { useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import { getWhereClauseForFieldName } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { useGetUser } from 'v2/core/services/user';

interface DataListItemProps {
  label: string;
  value?: number;
  id: string;
  fieldName: FieldNames;
  labelCategory?: Category;
  dateRange: TimeRange;
  baseWhereClause: WhereClause;
  showFeedbackDrawer?: (
    id: string,
    label: string,
    category?: Category,
    isUpdateInProgress?: boolean,
    isVisible?: boolean,
  ) => void;
  shouldShowAddToFilter?: boolean;
  shouldDisableInteraction?: boolean;
  isVisible?: boolean;
  isUpdateInProgress?: boolean;
}

export const DataListItem = ({
  label,
  value,
  fieldName,
  id,
  labelCategory,
  dateRange,
  baseWhereClause,
  showFeedbackDrawer,
  shouldShowAddToFilter = true,
  shouldDisableInteraction = false,
  isVisible,
  isUpdateInProgress,
}: DataListItemProps) => {
  const theme = useTheme();

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [isHovering, setIsHovering] = useState(false);
  const addQuickFilterV2 = useAddQuickFilter();

  const whereClause = combineWhereClauses(
    baseWhereClause,
    getWhereClauseForFieldName({
      fieldName: fieldName,
      values: [id],
      op: OperationType.AND,
    }),
    OperationType.AND,
  );

  const eqlExpression = useGetEQLExpression({
    tenantID,
    eqlProps: {
      expressionType: ExpressionType.Aggregates,
      dateRange,
      whereClause,
    },
  });

  const shouldShowCategory = !!labelCategory;

  const appendFilter = () => {
    if (fieldName && id) {
      const quickFilterParams = [
        {
          field: fieldName,
          values: [id],
        },
      ];

      addQuickFilterV2(quickFilterParams);
      trackQuickFiltersEvents({
        eventName: 'Quick Filter Added',
        eqlExpression: eqlExpression.eql,
        triggerType: 'Table',
      });
    }
  };

  const renderFeedbackCount = () => {
    if (shouldDisableInteraction) {
      return <Typography sx={{ px: 3 }}>{value ?? 0}</Typography>;
    }
    return (
      <Tooltip title="View Feedback" placement="top" disableInteractive>
        <Link
          component="button"
          sx={{ px: 3 }}
          onClick={() =>
            showFeedbackDrawer?.(
              id,
              label,
              labelCategory,
              isUpdateInProgress,
              isVisible,
            )
          }
          underline="hover"
          fontSize={16}
        >
          {value ?? 0}
        </Link>
      </Tooltip>
    );
  };

  const renderSecondaryAction = () => {
    if (shouldDisableInteraction) {
      return;
    }
    if (!shouldShowAddToFilter) {
      return;
    }
    return (
      <ListItemIcon
        sx={{
          minWidth: '40px',
        }}
      >
        <Tooltip title="Add to filter" placement="top" disableInteractive>
          <IconButton
            edge="end"
            sx={{ visibility: isHovering ? 'visible' : 'hidden' }}
            onClick={appendFilter}
          >
            <FilterAltRounded />
          </IconButton>
        </Tooltip>
      </ListItemIcon>
    );
  };

  const isInvisibleLabel = isVisible === false;
  const isUpdateInProgressLabel =
    isUpdateInProgress === true && !isInvisibleLabel;

  const getLabelTooltipTitle = () => {
    return (
      <Stack spacing={1}>
        <Box>
          {shouldShowCategory ? (
            <CategoryLabelTooltipNode
              text={label}
              category={labelCategory}
              iconSize={14}
            />
          ) : (
            label
          )}
        </Box>
        {isInvisibleLabel && (
          <Box>
            {getInfoMessageForLabelState({
              labelType: FieldNames.Reason
                ? LabelType.Reason
                : LabelType.Aspect,
              labelState: 'invisible',
            })}
          </Box>
        )}
      </Stack>
    );
  };

  const labelNode = (
    <Stack direction="row" alignItems="baseline" columnGap={0.5}>
      <Tooltip
        title={getLabelTooltipTitle()}
        disableInteractive
        placement="top-start"
      >
        <Stack direction="row" alignItems="baseline" columnGap={0.5}>
          {labelCategory && (
            <Box>
              <CategoryIcon
                category={labelCategory}
                size={14}
                fill={
                  isInvisibleLabel ? theme.palette.text.disabled : undefined
                }
              />
            </Box>
          )}
          <Typography
            variant="body1"
            color={isInvisibleLabel ? 'text.secondary' : 'text.primary'}
          >
            {label}
          </Typography>
        </Stack>
      </Tooltip>
      {isUpdateInProgressLabel && (
        <Box>
          <LabelUpdateInProgressIcon
            size={18}
            labelType={FieldNames.Reason ? LabelType.Reason : LabelType.Aspect}
          />
        </Box>
      )}
    </Stack>
  );

  return (
    <ListItem
      sx={{
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
      }}
      onMouseEnter={() => setIsHovering(true)}
      divider
      onMouseLeave={() => setIsHovering(false)}
    >
      <ListItemText
        primary={labelNode}
        primaryTypographyProps={{ variant: 'body1' }}
      />
      {renderFeedbackCount()}
      {renderSecondaryAction()}
    </ListItem>
  );
};
